import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Download, DollarSign, TrendingUp, ShoppingBag, Percent } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const fmt = (n) => (n ?? 0).toLocaleString(undefined, { style: "currency", currency: "USD" });
const pct = (n) => `${(n ?? 0).toFixed(1)}%`;

// Preset -> [startISO, endISO]
const presetRange = (preset) => {
  const now = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (preset) {
    case "today": return [iso(startOfToday), iso(startOfToday)];
    case "week": {
      const s = new Date(startOfToday); s.setDate(s.getDate() - 6); return [iso(s), iso(startOfToday)];
    }
    case "month": {
      const s = new Date(now.getFullYear(), now.getMonth(), 1); return [iso(s), iso(startOfToday)];
    }
    case "last_month": {
      const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const e = new Date(now.getFullYear(), now.getMonth(), 0);
      return [iso(s), iso(e)];
    }
    case "90d": {
      const s = new Date(startOfToday); s.setDate(s.getDate() - 89); return [iso(s), iso(startOfToday)];
    }
    case "ytd": {
      const s = new Date(now.getFullYear(), 0, 1); return [iso(s), iso(startOfToday)];
    }
    default: return [null, null];
  }
};

function KpiTile({ icon: Icon, label, value, sub, tone = "slate" }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    violet: "bg-violet-50 text-violet-700 border-violet-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
  };
  return (
    <div className={`rounded-xl border p-3 ${tones[tone]}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide opacity-80">
        <Icon className="w-4 h-4" />
        {label}
      </div>
      <p className="text-2xl font-bold mt-1">{value}</p>
      {sub && <p className="text-xs opacity-70">{sub}</p>}
    </div>
  );
}

function BreakdownTable({ title, rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <Card>
      <CardContent className="p-3">
        <p className="text-sm font-semibold mb-2">{title}</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid={`finance-table-${title.toLowerCase().replace(/\s+/g,'-')}`}>
            <thead className="text-xs text-slate-500 border-b">
              <tr>
                <th className="py-1.5 text-left">Name</th>
                <th className="py-1.5 text-right">Revenue</th>
                <th className="py-1.5 text-right">Cost</th>
                <th className="py-1.5 text-right">Markup</th>
                <th className="py-1.5 text-right">%</th>
                <th className="py-1.5 text-right">Lines</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 15).map((r) => (
                <tr key={r.name} className="border-b border-slate-100 last:border-0">
                  <td className="py-1.5 truncate max-w-[8rem]">{r.name}</td>
                  <td className="py-1.5 text-right">{fmt(r.revenue)}</td>
                  <td className="py-1.5 text-right text-slate-500">{fmt(r.cost)}</td>
                  <td className="py-1.5 text-right font-semibold text-emerald-700">{fmt(r.markup)}</td>
                  <td className="py-1.5 text-right text-emerald-700">{pct(r.markup_pct)}</td>
                  <td className="py-1.5 text-right text-slate-500">{r.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

export default function FinanceReport() {
  const [preset, setPreset] = useState("month");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const range = useMemo(() => {
    if (preset === "custom") return [startDate || null, endDate || null];
    return presetRange(preset);
  }, [preset, startDate, endDate]);

  useEffect(() => {
    if (preset !== "custom") {
      const [s, e] = presetRange(preset);
      setStartDate(s || ""); setEndDate(e || "");
    }
  }, [preset]);

  const load = async () => {
    setLoading(true);
    try {
      const [s, e] = range;
      const params = {};
      if (s) params.start = s;
      if (e) params.end = e;
      const res = await axios.get("/finance/report", { params });
      setReport(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load finance report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [preset, startDate, endDate]);

  const exportCsv = () => {
    if (!report) return;
    const lines = [];
    const s = report.summary;
    lines.push("Section,Metric,Value");
    lines.push(`Summary,Revenue,${s.revenue}`);
    lines.push(`Summary,Cost,${s.cost}`);
    lines.push(`Summary,Markup,${s.markup}`);
    lines.push(`Summary,Avg Markup %,${s.avg_markup_pct}`);
    lines.push(`Summary,Orders,${s.orders_count}`);
    lines.push(`Summary,Line Items,${s.line_items_count}`);
    lines.push("");
    ["by_supplier", "by_tech", "by_brand"].forEach((k) => {
      const title = k.replace("by_", "").toUpperCase();
      lines.push(`${title},Name,Revenue,Cost,Markup,Markup %,Lines`);
      (report[k] || []).forEach((r) =>
        lines.push(`${title},"${r.name.replace(/"/g, '""')}",${r.revenue},${r.cost},${r.markup},${r.markup_pct},${r.count}`)
      );
      lines.push("");
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `finance_${range[0] || "all"}_to_${range[1] || "now"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded");
  };

  return (
    <div className="space-y-4">
      {/* Header + filter */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold">Finance</h2>
          <p className="text-xs text-slate-500">Revenue, cost and markup across ordered / delivered / installed parts.</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-xs">Range</Label>
            <select
              value={preset}
              onChange={(e) => setPreset(e.target.value)}
              className="w-full border rounded-md px-3 py-2 text-sm bg-white h-10"
              data-testid="finance-preset"
            >
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="last_month">Last Month</option>
              <option value="90d">Last 90 days</option>
              <option value="ytd">Year-to-date</option>
              <option value="custom">Custom range</option>
            </select>
          </div>
          {preset === "custom" && (
            <>
              <div>
                <Label className="text-xs">From</Label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-10" data-testid="finance-start" />
              </div>
              <div>
                <Label className="text-xs">To</Label>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="h-10" data-testid="finance-end" />
              </div>
            </>
          )}
          <Button onClick={exportCsv} variant="outline" className="h-10" disabled={!report} data-testid="finance-export">
            <Download className="w-4 h-4 mr-1" /> CSV
          </Button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-6 h-6 animate-spin text-violet-600" />
        </div>
      )}

      {!loading && report && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <KpiTile icon={DollarSign} tone="emerald" label="Revenue" value={fmt(report.summary.revenue)} sub={`${report.summary.orders_count} orders`} />
            <KpiTile icon={ShoppingBag} tone="slate" label="Cost" value={fmt(report.summary.cost)} />
            <KpiTile icon={TrendingUp} tone="violet" label="Markup" value={fmt(report.summary.markup)} sub={`${report.summary.line_items_count} line items`} />
            <KpiTile icon={Percent} tone="amber" label="Avg Markup" value={pct(report.summary.avg_markup_pct)} />
          </div>

          {/* Chart */}
          {report.timeseries.length > 0 ? (
            <Card>
              <CardContent className="p-3">
                <p className="text-sm font-semibold mb-2">Daily Revenue vs. Markup</p>
                <div style={{ width: "100%", height: 240 }}>
                  <ResponsiveContainer>
                    <LineChart data={report.timeseries} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(v) => fmt(v)} />
                      <Legend />
                      <Line type="monotone" dataKey="revenue" stroke="#059669" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="markup" stroke="#7c3aed" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-6 text-center text-sm text-slate-500">
                No orders in this range yet.
              </CardContent>
            </Card>
          )}

          {/* Breakdown tables */}
          <div className="grid gap-3 md:grid-cols-3">
            <BreakdownTable title="By Supplier" rows={report.by_supplier} />
            <BreakdownTable title="By Tech" rows={report.by_tech} />
            <BreakdownTable title="By Brand" rows={report.by_brand} />
          </div>
        </>
      )}
    </div>
  );
}
